import { Component, computed, inject, model, effect } from '@angular/core';
import { AuthStore } from 'projects/shared/src/lib/store/auth.store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { IsActiveMatchOptions, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { RouterHeorotPagesEnum } from 'projects/shared/src/lib/infrastructure/enums/router-heorot-pages.enum';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { environment } from '../../environments/environment';
import { AvatarModule } from 'primeng/avatar';
import { HeorotRouterEnum } from '../../infrastructure/enums/router-link.enum';
import { LocalStorageService, SELECTED_LANG } from 'projects/shared/src/lib/core/services/local-storage.service';
import { ChangelogUpdatesService } from 'projects/shared/src/lib/core/services/changelog-updates.service';

type SupportedLanguage = 'nl' | 'nl-be' | 'en';

@Component({
  selector: 'app-side-navigation',
  imports: [TranslatePipe, RouterLink, RouterLinkActive, MenuModule, AvatarModule],
  templateUrl: './side-navigation.component.html',
  styleUrl: './side-navigation.component.scss',
})
export class SideNavigationComponent {
  public readonly routeMatchOptions: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'ignored',
    matrixParams: 'ignored',
    fragment: 'ignored',
  };

  private readonly supportedLanguageOptions = [
    { code: 'nl', label: 'Languages.nl' },
    { code: 'nl-be', label: 'Languages.nl-be' },
    { code: 'en', label: 'Languages.en' },
  ] as const;
  private readonly supportedLanguages: SupportedLanguage[] = this.supportedLanguageOptions.map(
    ({ code }) => code
  );
  private readonly translate = inject(TranslateService);
  private readonly localStorageService = inject(LocalStorageService);
  private primengConfig = inject(PrimeNG);
  private router = inject(Router);

  private readonly authStore = inject(AuthStore);
  public isAuthenticated = computed(() => this.authStore.authInfo().isAuthenticated);
  public firstName = computed(() => this.authStore.authInfo()?.userData?.given_name ?? '');
  public prefix = computed(
    () => this.authStore.account()?.prefix ?? this.authStore.authInfo()?.userData?.middle_name ?? ''
  );
  public lastName = computed(() => this.authStore.authInfo()?.userData?.family_name ?? '');
  public lastNameWithPrefix = computed(() =>
    [this.prefix(), this.lastName()].filter(Boolean).join(' ')
  );

  public initials = computed(
    () =>
      (this.firstName() ? this.firstName().charAt(0).toUpperCase() : '') +
      (this.lastName() ? this.lastName().charAt(0).toUpperCase() : '')
  );
  public readonly hasUnseenChangelog = inject(ChangelogUpdatesService).hasUnseenUpdates;
  public language = model<SupportedLanguage>(this.getInitialLanguage());
  public helpItems!: MenuItem[];
  public lexipoortItems!: MenuItem[];
  public languageItems!: MenuItem[];

  constructor() {
    this._initializeTranslations();
    this._watchLanguageChanges();
    this.buildMenus();
    this.translate.onLangChange.subscribe(() => this.buildMenus());
  }

  private getInitialLanguage(): SupportedLanguage {
    const persistedLanguage = this.localStorageService.getPersistentItem(SELECTED_LANG);
    return this.isSupportedLanguage(persistedLanguage) ? persistedLanguage : 'nl';
  }

  private async _initializeTranslations(): Promise<void> {
    const languages = this.supportedLanguages;
    this.translate.addLangs([...languages]);
    this.translate.setFallbackLang(this.language());

    // Manual fetch + setTranslation preload disabled.
    // Translation loading is now handled by the configured ngx-translate loader.
    // await Promise.all(
    //   languages.map(async (lang) => {
    //     const [baseTranslations, taskContentTranslations] = await Promise.all([
    //       this._loadTranslations(`assets/i18n/${lang}.json`),
    //       this._loadTranslations(`assets/i18n/tasks-content/${lang}.json`),
    //     ]);
    //     const translations = { ...baseTranslations, ...taskContentTranslations };
    //     this.translate.setTranslation(lang, translations);
    //   })
    // );

    const userId = this.authStore.account()?.identityId;
    const userLang = userId ? this.localStorageService.getUserScopedItem(SELECTED_LANG, userId) : null;
    const globalLang = this.localStorageService.getPersistentItem(SELECTED_LANG);
    const lang = userLang || globalLang;

    if (this.isSupportedLanguage(lang)) {
      this.language.set(lang);
      this.localStorageService.setPersistentItem(SELECTED_LANG, lang);
    }

    this.translate.use(this.language());
    this._translatePrimeNg();
  }

  private _watchLanguageChanges(): void {
    effect(() => {
      this.translate.use(this.language());
      this._translatePrimeNg();
    });
  }

  private async _loadTranslations(path: string): Promise<object> {
    try {
      const response = await fetch(path);
      if (!response.ok) throw new Error(`Failed to load ${path}`);
      return await response.json();
    } catch (error) {
      console.error('Error loading translations:', error);
      return {};
    }
  }

  private isSupportedLanguage(language: string | null): language is SupportedLanguage {
    return !!language && this.supportedLanguages.some((supportedLanguage) => supportedLanguage === language);
  }

  private selectLanguage(language: SupportedLanguage) {
    this.language.set(language);
    this.translate.use(language);
    this.localStorageService.setPersistentItem(SELECTED_LANG, language);
    const userId = this.authStore.account()?.identityId;
    if (userId) {
      this.localStorageService.setUserScopedItem(SELECTED_LANG, language, userId);
    }
  }

  private buildMenus() {
    this.helpItems = [
      {
        label: this.translate.instant('Navigation.Feedback'),
        id: 'feedback',
        icon: 'ph ph-chat-teardrop-text ph-bold',
        command: () => window.open(environment.lexipoort.feedback),
      },
      {
        label: this.translate.instant('Navigation.Manual'),
        id: 'manual',
        icon: 'ph ph-book-open-text ph-bold',
        command: () => window.open(environment.lexipoort.manuals),
      },
      // HEO-868 - temporary hidden
      // {
      //   label: this.translate.instant('Navigation.Roadmap'),
      //   id: 'roadmap',
      //   icon: 'ph ph-flag ph-bold', // after release, add 'ph-fill'
      //   command: () => window.open(environment.lexima.roadmap),
      // },
    ];

    this.lexipoortItems = [
      {
        label: this.translate.instant('Navigation.TileOverview'),
        id: 'tile-overview',
        icon: 'ph ph-arrow-bend-up-left ph-bold',
        command: () => window.open(environment.lexipoort.dashboard),
      },
      {
        label: this.translate.instant('Navigation.Knowledge'),
        id: 'knowledge',
        icon: 'ph ph-lightbulb-filament ph-bold',
        command: () => window.open(environment.lexipoort.knowledgeBase),
      },
    ];

    this.languageItems = this.supportedLanguageOptions.map(({ code, label }) => ({
      label: this.translate.instant(label),
      icon: code === this.language() ? 'ph ph-check ph-bold' : undefined,
      command: () => this.selectLanguage(code),
    }));
  }

  private _translatePrimeNg() {
    this.translate.get('primeng').subscribe((res) => this.primengConfig.setTranslation(res));
  }

  public login() {
    this.authStore.authorize();
  }

  public logout() {
    this.authStore.centralLogout();
  }

  showChangelog() {
    this.router.navigate([HeorotRouterEnum.Changelog]);
  }

  public currentLanguageLabel() {
    const label =
      this.supportedLanguageOptions.find(({ code }) => code === this.language())?.label ??
      this.supportedLanguageOptions[0].label;

    return this.translate.instant(label);
  }

  public get planningUrl() {
    return `/${RouterHeorotPagesEnum.Planning}`;
  }

  public get resultsUrl() {
    return `/${RouterHeorotPagesEnum.Results}`;
  }

  public get planningAndResultsUrl() {
    return `/${RouterHeorotPagesEnum.PlanningAndResults}`;
  }

  public get dashboardUrl() {
    return `/${RouterHeorotPagesEnum.Dashboard}`;
  }

  public get settingsUrl() {
    return `/${RouterHeorotPagesEnum.Settings}`;
  }

  public positionMenu(btn: HTMLButtonElement, dropdownSelector: string) {
    setTimeout(() => {
      const btnRect = btn.getBoundingClientRect();
      const overlay = document.querySelector(dropdownSelector) as HTMLElement;
      if (overlay) {
        overlay.style.left = `${btnRect.right}px`;
        overlay.style.top = `${btnRect.top}px`;
      }
    }, 0);
  }
}
